import React, { useEffect, useState } from "react";
import { Formik, Form, FieldArray } from "formik";
import * as yup from "yup";
import clsx from "clsx";

import {
  Box,
  Typography,
  Grid,
  Button,
  Checkbox,
  FormControlLabel,
  Divider,
  Paper,
  CircularProgress
} from "@material-ui/core";

import { MyTextField } from "../../../../components/custom/MyTextField";
import { MyAlert } from "../../../../components/custom/MyAlert";
import { useStyles } from "../../../../shared/styles/useStyles";
import { WorkScheduleFormValues } from "../../../../module/superAdmin/form/WorkScheduleFormValues";

import { useDispatch, useSelector } from "react-redux";
import {
  startLoadWorkSchedule,
  startSetWorkSchedule,
} from "../../../../redux/actions/entitiesActions";
import {
  setErrorMsg,
  setSuccessMsg,
  uiCloseErrorAlert,
  uiCloseSuccessAlert,
} from "../../../../redux/actions/uiActions";
import { AppState } from "../../../../redux/reducers/rootReducer";
import { useIntl, FormattedMessage } from "react-intl";

const days = [
  { labelId: "Day.Monday", value: "Monday" },
  { labelId: "Day.Tuesday", value: "Tuesday" },
  { labelId: "Day.Wednesday", value: "Wednesday" },
  { labelId: "Day.Thursday", value: "Thursday" },
  { labelId: "Day.Friday", value: "Friday" },
  { labelId: "Day.Saturday", value: "Saturday" },
  { labelId: "Day.Sunday", value: "Sunday" },
];

const validationSchema = yup.object().shape({
  days: yup.array().of(
    yup.object().shape({
      startTime: yup
        .string()
        .when("isWorkingDay", {
          is: false,
          then: yup.string().required("Obligatorio"),
          otherwise: yup.string().notRequired(),
        }),
      endTime: yup
        .string()
        .when("isWorkingDay", {
          is: false,
          then: yup
            .string()
            .required("Obligatorio")
            .test(
              "is-after-start",
              "Hora fin debe ser mayor a inicio",
              function (value) {
                const { startTime } = this.parent;
                return !startTime || !value || startTime < value;
              }
            ),
          otherwise: yup.string().notRequired(),
        }),
      isWorkingDay: yup.boolean(),
    })
  ),
  restrictOutsideWorkHours: yup.boolean(),
});

const defaultValues: WorkScheduleFormValues = {
  days: days.map(() => ({
    startTime: "",
    endTime: "",
    isWorkingDay: false,
  })),
  restrictOutsideWorkHours: false,
};

export const WorkScheduleForm = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const intl = useIntl();
  const user = useSelector((state: AppState) => state.auth.user);
  const { successAlert, successMsg, errorAlert } = useSelector((state: AppState) => state.ui);

  const [initialValues, setInitialValues] = useState<WorkScheduleFormValues>(defaultValues);

  useEffect(() => {
    const fetchData = async () => {
      if (user?.idEntity) {
        const response: any = await dispatch<any>(startLoadWorkSchedule());
        if (response) {
          const loadedValues: WorkScheduleFormValues = {
            days: days.map((day) => {
              const match = response.schedule.find((d: any) => d.day === day.value);
              return {
                startTime: match?.startTime || "",
                endTime: match?.endTime || "",
                isWorkingDay: match ? !match.isWorkingDay : false,
              };
            }),
            restrictOutsideWorkHours: response.restrictOutsideWorkHours || false,
          };
          setInitialValues(loadedValues);
        }
      }
    };
    fetchData();
  }, [dispatch, user?.idEntity]);

  const handleSubmit = async (values: WorkScheduleFormValues, { setSubmitting }: any) => {
    if (!user?.idEntity) return;

    try {
      const response = await dispatch<any>(startSetWorkSchedule(values));
      if (response?.ok) {
        dispatch(setSuccessMsg(intl.formatMessage({ id: "WorkScheduleUpdated" })));
      }
    } catch (error) {
      dispatch(setErrorMsg(intl.formatMessage({ id: "WorkScheduleError" })));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper className={classes.paper}>
      <Box p={3}>
        <Typography variant="h6" gutterBottom align="center">
          <FormattedMessage id="WorkScheduleTitle" />
        </Typography>
        <Divider style={{ marginBottom: "1.5rem" }} />

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form className={classes.input} style={{ position: "relative" }}>
              {isSubmitting && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "rgba(255,255,255,0.6)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 10,
                  }}
                >
                  <CircularProgress />
                </div>
              )}

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={3}><strong><FormattedMessage id="Day" /></strong></Grid>
                <Grid item xs={3}><strong><FormattedMessage id="StartHour" /></strong></Grid>
                <Grid item xs={3}><strong><FormattedMessage id="EndHour" /></strong></Grid>
                <Grid item xs={3}><strong><FormattedMessage id="NonWorking" /></strong></Grid>

                <FieldArray
                  name="days"
                  render={() =>
                    days.map((day, index) => {
                      const disabled = values.days[index].isWorkingDay;
                      return (
                        <React.Fragment key={day.value}>
                          <Grid item xs={3}>
                            <FormattedMessage id={day.labelId} />
                          </Grid>
                          <Grid item xs={3}>
                            <MyTextField
                              name={`days[${index}].startTime`}
                              type="time"
                              size="small"
                              variant="outlined"
                              fullWidth
                              disabled={disabled}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <MyTextField
                              name={`days[${index}].endTime`}
                              type="time"
                              size="small"
                              variant="outlined"
                              fullWidth
                              disabled={disabled}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={3}>
                            <Checkbox
                              checked={values.days[index].isWorkingDay}
                              onChange={(e) => {
                                const checked = e.target.checked;

                                setFieldValue(`days[${index}].startTime`, "");
                                setFieldValue(`days[${index}].endTime`, "");

                                setTimeout(() => {
                                  setFieldValue(`days[${index}].isWorkingDay`, checked);
                                }, 0);
                              }}
                              color="primary"
                            />
                          </Grid>
                        </React.Fragment>
                      );
                    })
                  }
                />

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.restrictOutsideWorkHours}
                        onChange={(e) =>
                          setFieldValue("restrictOutsideWorkHours", e.target.checked)
                        }
                        color="primary"
                      />
                    }
                    label={<FormattedMessage id="RestrictOutOfHours" />}
                  />
                </Grid>

                <Grid item xs={12} style={{ textAlign: "center" }}>
                  <Button
                    className={clsx(classes.btn, classes.save)}
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                  >
                    <FormattedMessage id="Save" />
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>

        <MyAlert
          open={successAlert}
          typeAlert="success"
          message={successMsg ?? ""}
          time={2000}
          handleClose={() => dispatch(uiCloseSuccessAlert())}
        />

        <MyAlert
          open={errorAlert}
          typeAlert="error"
          message={successMsg ?? ""}
          time={3000}
          handleClose={() => dispatch(uiCloseErrorAlert())}
        />
      </Box>
    </Paper>
  );
};
